/**
 * Mock customer-note service — backed by the in-memory `mockNotes` fixtures.
 * Returns copies and simulates latency; never mutates the shared array (the tab
 * owns React state).
 */

import { mockNotes } from '@/data/mockNotes'
import { mockDelay } from '@/services/factory'
import type { NoteService } from './notes.service'
import type { CustomerNote, UpdateNoteInput } from '@/types/note.types'

export function createMockNoteService(): NoteService {
  return {
    async listForCustomer(customerId: string): Promise<CustomerNote[]> {
      await mockDelay(300)
      return mockNotes
        .filter((n) => n.customerId === customerId)
        .sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1
          if (!a.isPinned && b.isPinned) return 1
          return +new Date(b.createdAt) - +new Date(a.createdAt)
        })
        .map((n) => ({ ...n }))
    },

    async create(customerId: string, body: string): Promise<CustomerNote> {
      await mockDelay(300)
      return {
        id: `note-${Date.now()}`,
        customerId,
        body,
        isPinned: false,
        authorName: 'You',
        createdAt: new Date().toISOString(),
      }
    },

    async update(_customerId: string, id: string, input: UpdateNoteInput): Promise<CustomerNote> {
      await mockDelay(250)
      const existing = mockNotes.find((n) => n.id === id)
      return {
        id,
        customerId: existing?.customerId || 'unknown',
        body: input.body ?? existing?.body ?? '',
        isPinned: input.isPinned ?? existing?.isPinned ?? false,
        authorName: existing?.authorName ?? 'You',
        createdAt: existing?.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    },

    async remove(_customerId: string, _id: string): Promise<void> {
      await mockDelay(250)
    },
  }
}
